import React, { useEffect, useRef } from 'react';
import { Modal, Button, Row, Col, Typography } from 'antd';
import { TrophyOutlined, CloseOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

/**
 * AchievementUnlockedModal Component
 * Displays a modal when achievements are unlocked
 * 
 * @param {boolean} visible - Whether the modal is visible
 * @param {Array<Object>} achievements - Array of achievement objects
 * @param {Function} onClose - Callback when modal is closed
 */
const AUTO_CLOSE_MS = 2000;

const AchievementUnlockedModal = React.memo(({ visible, achievements, onClose }) => {
    const onCloseRef = useRef(onClose);

    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    // Use a ref for onClose so the timer is not reset on every parent re-render
    useEffect(() => {
        if (!visible || !achievements?.length) return undefined;

        const timer = setTimeout(() => {
            onCloseRef.current?.();
        }, AUTO_CLOSE_MS);

        return () => clearTimeout(timer);
    }, [visible, achievements]);

    if (!achievements || achievements.length === 0) return null;

    // Get primary color from first achievement for the modal background
    const primaryColor = achievements[0]?.badge_color || '#1890ff';
    const totalPoints = achievements.reduce((sum, a) => sum + (a.points || 0), 0);

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            width={achievements.length === 1 ? 380 : achievements.length === 2 ? 600 : 800}
            centered
            maskClosable
            closable
            closeIcon={<CloseOutlined style={{ color: '#fff', fontSize: 16 }} />}
            style={{
                top: 0
            }}
            bodyStyle={{
                padding: 0,
                overflow: 'hidden'
            }}
            wrapClassName="achievement-modal-wrapper"
        >
            <div style={{
                position: 'relative',
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                borderRadius: '20px',
                padding: achievements.length > 1 ? '28px 20px' : '32px 24px',
                textAlign: 'center',
                overflow: 'hidden',
                animation: 'achievementPopIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                maxHeight: '85vh',
                overflowY: 'auto'
            }}>
                {/* Shimmer effect */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                    animation: 'shimmer 2s infinite',
                    pointerEvents: 'none'
                }} />

                {/* Sparkle effects */}
                <div style={{
                    position: 'absolute',
                    top: '20%',
                    left: '15%',
                    fontSize: '20px',
                    animation: 'sparkle 1.5s ease-in-out infinite',
                    animationDelay: '0.2s'
                }}>✨</div>
                <div style={{
                    position: 'absolute',
                    top: '25%',
                    right: '20%',
                    fontSize: '16px',
                    animation: 'sparkle 1.5s ease-in-out infinite',
                    animationDelay: '0.5s'
                }}>⭐</div>
                <div style={{
                    position: 'absolute',
                    bottom: '30%',
                    left: '20%',
                    fontSize: '18px',
                    animation: 'sparkle 1.5s ease-in-out infinite',
                    animationDelay: '0.8s'
                }}>✨</div>
                <div style={{
                    position: 'absolute',
                    bottom: '25%',
                    right: '15%',
                    fontSize: '20px',
                    animation: 'sparkle 1.5s ease-in-out infinite',
                    animationDelay: '1.1s'
                }}>⭐</div>

                {/* Header */}
                {achievements.length > 1 && (
                    <div style={{
                        position: 'relative',
                        zIndex: 1,
                        marginBottom: '20px'
                    }}>
                        <TrophyOutlined style={{ 
                            fontSize: '40px', 
                            color: '#ffd700', 
                            marginBottom: '8px',
                            textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                        }} />
                        <Title level={4} style={{ 
                            color: '#fff', 
                            margin: 0,
                            fontSize: '18px',
                            fontWeight: 'bold',
                            textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                        }}>
                            {achievements.length} Achievements Unlocked! 🎉
                        </Title>
                    </div>
                )}

                {/* Achievements List */}
                <div style={{
                    position: 'relative',
                    zIndex: 1,
                    marginBottom: '20px'
                }}>
                    <Row gutter={[16, 16]} justify="center">
                        {achievements.map((achievement, index) => (
                            <Col 
                                key={index}
                                xs={24}
                                sm={achievements.length === 1 ? 24 : achievements.length === 2 ? 12 : 8}
                                style={{
                                    animation: `achievementPopIn ${0.6 + (index * 0.1)}s cubic-bezier(0.34, 1.56, 0.64, 1)`
                                }}
                            >
                                <div style={{ textAlign: 'center' }}>
                                    {/* Badge/Medal Circle */}
                                    <div style={{
                                        width: achievements.length > 1 ? '90px' : '120px',
                                        height: achievements.length > 1 ? '90px' : '120px',
                                        margin: '0 auto 12px',
                                        borderRadius: '50%',
                                        background: `linear-gradient(135deg, #fff 0%, ${achievement.badge_color}15 100%)`,
                                        border: '4px solid rgba(255, 255, 255, 0.9)',
                                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 2px 8px rgba(255, 255, 255, 0.5)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        animation: `badgeRotate ${0.8 + (index * 0.1)}s ease-out`,
                                        transform: 'scale(1)'
                                    }}>
                                        <div style={{ fontSize: achievements.length > 1 ? '48px' : '64px', lineHeight: 1 }}>
                                            {achievement.icon}
                                        </div>
                                    </div>

                                    {/* Achievement Title */}
                                    <Title level={achievements.length > 1 ? 5 : 3} style={{ 
                                        color: '#fff', 
                                        margin: '0 0 6px 0',
                                        fontSize: achievements.length > 1 ? '16px' : '22px',
                                        fontWeight: 'bold',
                                        textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        {achievement.name}
                                    </Title>

                                    {/* Achievement Description */}
                                    <Text style={{ 
                                        color: 'rgba(255, 255, 255, 0.95)',
                                        fontSize: achievements.length > 1 ? '11px' : '13px',
                                        display: 'block',
                                        marginBottom: '8px',
                                        lineHeight: '1.4',
                                        textShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
                                        minHeight: achievements.length > 1 ? '32px' : 'auto'
                                    }}>
                                        {achievement.description}
                                    </Text>

                                    {/* Points Badge */}
                                    <div style={{
                                        display: 'inline-block',
                                        background: 'rgba(255, 255, 255, 0.25)',
                                        backdropFilter: 'blur(10px)',
                                        borderRadius: '20px',
                                        padding: '6px 14px',
                                        border: '1px solid rgba(255, 255, 255, 0.3)'
                                    }}>
                                        <Text strong style={{ 
                                            color: '#fff',
                                            fontSize: achievements.length > 1 ? '13px' : '16px',
                                            textShadow: '0 1px 4px rgba(0, 0, 0, 0.3)'
                                        }}>
                                            +{achievement.points} Points
                                        </Text>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>

                    {/* Total Points (if multiple achievements) */}
                    {achievements.length > 1 && (
                        <div style={{
                            marginTop: '16px',
                            padding: '12px',
                            background: 'rgba(255, 255, 255, 0.2)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: '12px',
                            border: '1px solid rgba(255, 255, 255, 0.3)'
                        }}>
                            <Text strong style={{ 
                                color: '#fff',
                                fontSize: '16px',
                                textShadow: '0 1px 4px rgba(0, 0, 0, 0.3)'
                            }}>
                                Total: +{totalPoints} Points
                            </Text>
                        </div>
                    )}
                </div>

                {/* Close Button */}
                <Button
                    type="primary"
                    size="large"
                    onClick={onClose}
                    style={{
                        width: '100%',
                        height: '44px',
                        borderRadius: '12px',
                        background: 'rgba(255, 255, 255, 0.95)',
                        border: 'none',
                        color: primaryColor,
                        fontWeight: 'bold',
                        fontSize: '15px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        zIndex: 1
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#fff';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
                    }}
                >
                    🎉 Awesome!
                </Button>
            </div>

            <style>{`
                @keyframes achievementPopIn {
                    0% {
                        opacity: 0;
                        transform: scale(0.3) translateY(-50px);
                    }
                    50% {
                        transform: scale(1.05) translateY(0);
                    }
                    100% {
                        opacity: 1;
                        transform: scale(1) translateY(0);
                    }
                }

                @keyframes shimmer {
                    0% {
                        left: -100%;
                    }
                    100% {
                        left: 100%;
                    }
                }

                @keyframes sparkle {
                    0%, 100% {
                        opacity: 0;
                        transform: scale(0) rotate(0deg);
                    }
                    50% {
                        opacity: 1;
                        transform: scale(1.2) rotate(180deg);
                    }
                }

                @keyframes badgeRotate {
                    0% {
                        transform: scale(0) rotate(-180deg);
                    }
                    60% {
                        transform: scale(1.15) rotate(10deg);
                    }
                    100% {
                        transform: scale(1) rotate(0deg);
                    }
                }

                .achievement-modal-wrapper .ant-modal-content {
                    border-radius: 20px;
                    overflow: hidden;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
                }

                .achievement-modal-wrapper .ant-modal-body {
                    padding: 0;
                }

                .achievement-modal-wrapper .ant-modal {
                    top: 50%;
                    transform: translateY(0%);
                    padding-bottom: 0;
                }

                .achievement-modal-wrapper .ant-modal-wrap {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .achievement-modal-wrapper .ant-modal-close {
                    top: 12px;
                    right: 12px;
                    z-index: 2;
                }

                .achievement-modal-wrapper .ant-modal-close:hover {
                    background: rgba(0, 0, 0, 0.15);
                    border-radius: 50%;
                }
            `}</style>
        </Modal>
    );
});

AchievementUnlockedModal.displayName = 'AchievementUnlockedModal';

export default AchievementUnlockedModal;

